import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Menu, X, CalendarDays, Instagram, Facebook } from "lucide-react";

const NAV = [
  ["/fitness", "nav.fitness"],
  ["/pilates", "nav.pilates"],
  ["/pricing", "nav.pricing"],
  ["/team", "nav.team"],
  ["/contact", "nav.contact"],
];

export function PublicLayout({ children }) {
  const { t, tl } = useI18n();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showFab, setShowFab] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowFab(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-40 border-b border-border/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" data-testid="brand-logo" className="font-display text-base font-bold tracking-tight sm:text-xl lg:text-2xl">
            Frenchies Fitness &amp; Pilates
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map(([to, key]) => (
              <Link key={to} to={to} data-testid={`nav-${key.split(".")[1]}`}
                className="text-sm font-medium text-foreground/80 transition-colors hover:text-primary">
                {t(key)}
              </Link>
            ))}
            <Link to="/schedule" data-testid="nav-schedule"
              className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-4 py-1.5 text-sm font-semibold text-gold transition-colors hover:bg-gold/20">
              <CalendarDays className="h-4 w-4" />{t("nav.schedule")}
            </Link>
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <ThemeToggle />
            <LanguageSwitcher />
            {user ? (
              <Button data-testid="header-dashboard-btn" onClick={() => navigate("/app")} className="rounded-full">
                {t("nav.dashboard")}
              </Button>
            ) : (
              <>
                <Button variant="ghost" data-testid="header-login-btn" onClick={() => navigate("/login")} className="rounded-full">
                  {t("nav.login")}
                </Button>
                <Button data-testid="header-signup-btn" onClick={() => navigate("/register")} className="rounded-full">
                  {t("nav.signup")}
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="menu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t border-border bg-background px-5 py-4 lg:hidden">
            <nav className="flex flex-col gap-3">
              <Link to="/schedule" onClick={() => setOpen(false)} data-testid="nav-schedule-mobile"
                className="flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-base font-semibold text-gold">
                <CalendarDays className="h-4 w-4" />{t("nav.schedule")}
              </Link>
              {NAV.map(([to, key]) => (
                <Link key={to} to={to} onClick={() => setOpen(false)} className="py-1 text-base font-medium">
                  {t(key)}
                </Link>
              ))}
              <div className="mt-2 flex items-center gap-3">
                <LanguageSwitcher />
                {user ? (
                  <Button onClick={() => navigate("/app")} className="rounded-full">{t("nav.dashboard")}</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full">{t("nav.login")}</Button>
                    <Button onClick={() => navigate("/register")} className="rounded-full">{t("nav.signup")}</Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-24 border-t border-border bg-secondary/40">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 md:grid-cols-4 lg:px-8">
          <div>
            <div className="font-display text-xl font-bold">{settings?.brand || "Frenchies"}</div>
            <p className="mt-3 max-w-xs text-sm text-muted-foreground">
              {settings?.texts?.tagline ? (settings.texts.tagline.es) : "Fitness & Pilates · Panamá"}
            </p>
            <address className="mt-3 text-sm not-italic text-muted-foreground" data-testid="footer-address">
              Coronado Padel Center, Nueva Gorgona<br />Coronado, Panamá Oeste<br />
              <a href="tel:+50768004547" className="hover:underline">+507 6800-4547</a>
            </address>
          </div>
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Frenchies</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/fitness">{t("nav.fitness")}</Link></li>
              <li><Link to="/pilates">{t("nav.pilates")}</Link></li>
              <li><Link to="/pricing">{t("nav.pricing")}</Link></li>
              <li><Link to="/team">{t("nav.team")}</Link></li>
              <li><Link to="/horarios" data-testid="footer-horarios">{tl({ es: "Horarios y ubicación", en: "Hours & location", fr: "Horaires et adresse" })}</Link></li>
            </ul>
          </div>
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Coronado</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/gym-coronado">Gym Coronado</Link></li>
              <li><Link to="/functional-movement-coronado">Functional Movement Coronado</Link></li>
              <li><Link to="/functional-fitness-center-coronado">Functional Fitness Center</Link></li>
              <li><Link to="/pilates-coronado">Pilates Coronado</Link></li>
              <li><Link to="/blog" data-testid="footer-blog">Blog</Link></li>
              <li><Link to="/legal/cgv">{t("footer.cgv")}</Link></li>
              <li><Link to="/legal/privacy">{t("footer.privacy")}</Link></li>
              <li><Link to="/faq">{t("nav.faq")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("nav.contact")}</div>
            <p className="text-sm text-muted-foreground">{settings?.contact?.email}</p>
            <p className="mb-3 text-sm text-muted-foreground">{settings?.contact?.phone}</p>
            <WhatsAppButton floating={false} />
            <div className="mt-4 flex items-center gap-3">
              <a href={settings?.social?.instagram || "https://www.instagram.com/frenchies.fitness"}
                target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card transition-colors hover:text-primary">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={settings?.social?.facebook || "https://www.facebook.com/frenchiesroostersfitness"}
                target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card transition-colors hover:text-primary">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} {settings?.brand || "Frenchies"}. {t("footer.rights")}
        </div>
      </footer>

      <AnimatePresence>
        {showFab && (
          <motion.button
            data-testid="schedule-button-floating"
            onClick={() => navigate("/schedule")}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-[0_8px_30px_hsl(var(--primary)/0.45)]"
          >
            <CalendarDays className="h-5 w-5" />{t("nav.schedule")}
          </motion.button>
        )}
      </AnimatePresence>

      <WhatsAppButton />
    </div>
  );
}
